import React from "react";
import { createRoot } from "react-dom/client";
import { StyledEngineProvider } from "@mui/material/styles";
import { ThemeProvider, createTheme } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { QuizApp } from "./apps/quizpage";
import { AccountApp } from "@/features/account/AccountPage";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { quizTheme } from "./theme/quizTheme";

import "./styles/index.scss";
import { installScrollOnNavigate } from "@/utils/scrollOnNavigate";

// Reset scroll position to the top on every SPA navigation (pushState / replaceState / popstate).
// Must be called once, before any React rendering begins.
installScrollOnNavigate();

const theme = createTheme({
  typography: {
    // Quiz body font (--pop-font-body), with the same fallback stack
    fontFamily: "var(--pop-font-body, 'Archivo', sans-serif)",
  },
  palette: {
    background: {
      default: "#faf5eb", // quiz cream
      paper: "#FFFCF5", // warm off-white for dialogs/drawers/menus
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          // Quiz button font + pill shape
          fontFamily: "var(--pop-font-button, 'Jost', 'Prompt', sans-serif)",
          borderRadius: "100px",
        },
      },
    },
  },
});

function safeJSONParse<T = any>(attr: string | null, fallback: T): T {
  if (!attr) return fallback;
  try {
    return JSON.parse(attr) as T;
  } catch (e) {
    console.error("Failed to parse JSON attribute:", e);
    return fallback;
  }
}

// -------------------------------
// QUIZ APP MOUNT
// -------------------------------
const quizMount = document.querySelector("[data-react-app='quizpage']");

if (quizMount) {
  document.body.setAttribute("data-quiz-page", "true");

  const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;

  if (isTouchDevice) {
    const style = document.createElement("style");
    style.textContent = `
      @media (hover: none) and (pointer: coarse) {
        * {
          -webkit-tap-highlight-color: transparent !important;
          -webkit-touch-callout: none !important;
        }
        button, [role="button"], a, .MuiButton-root, .MuiButtonBase-root, .MuiIconButton-root {
          cursor: default !important;
          touch-action: manipulation !important;
          -webkit-user-select: none !important;
          user-select: none !important;
        }
        *:hover {
          transition: none !important;
        }
        input, textarea, select {
          touch-action: manipulation !important;
        }
      }
    `;
    document.head.appendChild(style);

    document.addEventListener(
      "touchstart",
      (e) => {
        const target = e.target as HTMLElement | null;
        if (target) target.dataset.touching = "true";
      },
      { passive: true }
    );

    document.addEventListener(
      "touchend",
      (e) => {
        const target = e.target as HTMLElement | null;
        if (target && target.dataset.touching === "true") {
          const isClickable =
            target.tagName === "BUTTON" ||
            target.tagName === "A" ||
            target.getAttribute("role") === "button" ||
            target.closest("button") ||
            target.closest('[role="button"]') ||
            target.closest(".MuiButton-root") ||
            target.closest(".MuiIconButton-root") ||
            target.closest(".MuiButtonBase-root");

          if (isClickable) {
            const clickableElement =
              (target.closest(
                'button, [role="button"], .MuiButton-root, .MuiButtonBase-root'
              ) as HTMLElement) || target;

            e.preventDefault();
            e.stopPropagation();
            clickableElement.click();
          }

          delete target.dataset.touching;
        }
      },
      { passive: false }
    );
  }

  const fixThirdPartyModals = () => {
    const highZIndexElements = Array.from(document.querySelectorAll("*")).filter(
      (el) => {
        const zIndex = parseInt(window.getComputedStyle(el).zIndex, 10);
        return Number.isFinite(zIndex) && zIndex > 1000;
      }
    );

    highZIndexElements.forEach((el) => {
      const htmlEl = el as HTMLElement;
      htmlEl.style.pointerEvents = "auto";
      htmlEl.style.touchAction = "auto";

      const closeButtons = el.querySelectorAll(
        '[class*="close"], [class*="Close"], [aria-label*="close"], [aria-label*="Close"], button'
      );
      closeButtons.forEach((btn) => {
        const htmlBtn = btn as HTMLElement;
        htmlBtn.style.pointerEvents = "auto";
        htmlBtn.style.touchAction = "manipulation";
        htmlBtn.style.cursor = "pointer";
      });
    });
  };

  let modalFixTimeout: ReturnType<typeof setTimeout> | null = null;
  const observer = new MutationObserver(() => {
    if (modalFixTimeout) return;
    modalFixTimeout = setTimeout(() => {
      fixThirdPartyModals();
      modalFixTimeout = null;
    }, 500);
  });
  observer.observe(document.body, { childList: true, subtree: true });

  setTimeout(fixThirdPartyModals, 2000);
  setTimeout(fixThirdPartyModals, 5000);

  window.addEventListener("beforeunload", () => observer.disconnect());

  createRoot(quizMount).render(
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={quizTheme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <CssBaseline />
          <QuizApp
            customerJSON={safeJSONParse(quizMount.getAttribute("data-customer"), null)}
            bundleProductsJSON={safeJSONParse(
              quizMount.getAttribute("data-bundle-products"),
              []
            )}
            pouchesProductsJSON={safeJSONParse(
              quizMount.getAttribute("data-pouches-products"),
              []
            )}
            treatProductsJSON={safeJSONParse(
              quizMount.getAttribute("data-treat-products"),
              []
            )}
            supplementProductsJSON={safeJSONParse(
              quizMount.getAttribute("data-supplement-products"),
              []
            )}
            hiddenPriceProductsJSON={safeJSONParse(
              quizMount.getAttribute("data-hidden_price_products"),
              []
            )}
            planProductMap={safeJSONParse(
              quizMount.getAttribute("data-plan_product_map"),
              {}
            )}
            hiddenPriceMap={safeJSONParse(
              quizMount.getAttribute("data-hidden_price_map"),
              {}
            )}
          />
        </LocalizationProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

// -------------------------------
// ACCOUNT APP MOUNT
// -------------------------------
const accountMount = document.querySelector("[data-react-app='account']");

if (accountMount) {
  const customerJSON = safeJSONParse(accountMount.getAttribute("data-customer"), null);
  const shopDomain = accountMount.getAttribute("data-shop");
  const proxyBase = accountMount.getAttribute("data-proxy-base") || "/apps/paws";

  const bundleProducts = safeJSONParse(accountMount.getAttribute("data-bundle-products"), []);
  const pouchesProducts = safeJSONParse(accountMount.getAttribute("data-pouches-products"), []);
  const treatProducts = safeJSONParse(accountMount.getAttribute("data-treat-products"), []);

  const hiddenPriceProducts =
    safeJSONParse(accountMount.getAttribute("data-hidden-price-products"), null) ??
    safeJSONParse(accountMount.getAttribute("data-hidden_price_products"), []);

  const products = [
    ...(Array.isArray(bundleProducts) ? bundleProducts : []),
    ...(Array.isArray(pouchesProducts) ? pouchesProducts : []),
    ...(Array.isArray(treatProducts) ? treatProducts : []),
    ...(Array.isArray(hiddenPriceProducts) ? hiddenPriceProducts : []),
  ];

  createRoot(accountMount).render(
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <CssBaseline />
          <AccountApp
            customerJSON={customerJSON}
            shopDomain={shopDomain || null}
            proxyBase={proxyBase}
            products={products}
          />
        </LocalizationProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}